// client/src/modules/technician/routes.tsx
import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import { FormConsumerProvider } from '../../contexts/FormConsumerContext';

// Primary pages (in nav)
const TechToday = lazyWithRetry(() => import('../../pages/Technician/TechToday'));
const TechHistory = lazyWithRetry(() => import('../../pages/Technician/TechHistory'));
const CurationQueue = lazyWithRetry(() => import('../../pages/Technician/CurationQueue'));

// Detail pages (navigated to from cards)
const TechJobDetail = lazyWithRetry(() => import('../../pages/Technician/TechJobDetail'));
const TechInstallDetail = lazyWithRetry(() => import('../../pages/Technician/TechInstallDetail'));

// Generator lookup without a ticket — technician-scoped read, NOT the Field
// Service (P11) generator pages.
const TechGenerators = lazyWithRetry(() => import('../../pages/Technician/TechGenerators'));
const TechGeneratorDetail = lazyWithRetry(() => import('../../pages/Technician/TechGeneratorDetail'));

// Form consumer pages (FSR fill, view, edit — within technician module)
const ServiceReportLauncher = lazyWithRetry(() => import('../../pages/Forms/ServiceReportLauncher'));
const IncidentReportLauncher = lazyWithRetry(() => import('../../pages/Forms/IncidentReportLauncher'));
const FormFiller = lazyWithRetry(() => import('../../pages/Forms/FormFiller'));
const FormSubmissionList = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionList'));
const FormSubmissionDetail = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionDetail'));
const FormSubmissionEdit = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionEdit'));
const FormReportPage = lazyWithRetry(() => import('../../pages/Forms/FormReportPage'));

// Reports page
const TechReports = lazyWithRetry(() => import('../../pages/Technician/TechReports'));

// Legacy pages (kept for direct URL access, not in nav)
const MyJobs = lazyWithRetry(() => import('../../pages/Technician/MyJobs'));
const MySchedule = lazyWithRetry(() => import('../../pages/Technician/MySchedule'));
const BriefingList = lazyWithRetry(() => import('../../pages/Technician/BriefingList'));

/**
 * Wraps form pages so navigation stays within /technician/forms/*
 */
function TechFormsConsumer({ children }: { children: React.ReactNode }) {
  return (
    <FormConsumerProvider basePath="/technician/forms" moduleLabel="Service Reports" moduleId="P12">
      {children}
    </FormConsumerProvider>
  );
}

export const routes: RouteObject[] = [
  // Landing page — Today dashboard
  {
    index: true,
    element: <ProtectedRoute><TechToday /></ProtectedRoute>,
  },
  // History — past jobs with FSR status
  {
    path: 'history',
    element: <ProtectedRoute><TechHistory /></ProtectedRoute>,
  },
  // Job detail
  {
    path: 'jobs/:ticketId',
    element: <ProtectedRoute><TechJobDetail /></ProtectedRoute>,
  },
  // Install project detail
  {
    path: 'installs/:installId',
    element: <ProtectedRoute><TechInstallDetail /></ProtectedRoute>,
  },
  // Generator search + detail (technician-scoped; see TechGeneratorDetail)
  {
    path: 'generators',
    element: <ProtectedRoute><TechGenerators /></ProtectedRoute>,
  },
  {
    path: 'generators/:generatorId',
    element: <ProtectedRoute><TechGeneratorDetail /></ProtectedRoute>,
  },
  // Briefing detail — retired, redirect to today dashboard
  {
    path: 'briefings/:briefingId',
    element: <Navigate to="/technician" replace />,
  },
  // Curation (manager only)
  {
    path: 'curation',
    element: <ProtectedRoute requireManagerOrAdmin><CurationQueue /></ProtectedRoute>,
  },

  // ─── Form consumer routes (FSR within technician module) ──────────
  {
    path: 'start-report',
    element: <ProtectedRoute><TechFormsConsumer><ServiceReportLauncher /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'incident-report',
    element: <ProtectedRoute><TechFormsConsumer><IncidentReportLauncher /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/fill/:templateId',
    element: <ProtectedRoute><TechFormsConsumer><FormFiller /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions',
    element: <ProtectedRoute><TechFormsConsumer><FormSubmissionList /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId',
    element: <ProtectedRoute><TechFormsConsumer><FormSubmissionDetail /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/edit',
    element: <ProtectedRoute><TechFormsConsumer><FormSubmissionEdit /></TechFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/report',
    element: <ProtectedRoute><TechFormsConsumer><FormReportPage /></TechFormsConsumer></ProtectedRoute>,
  },

  // ─── Legacy routes (preserved for direct URL access) ──────────────
  {
    path: 'jobs',
    element: <ProtectedRoute><MyJobs /></ProtectedRoute>,
  },
  {
    path: 'schedule',
    element: <ProtectedRoute><MySchedule /></ProtectedRoute>,
  },
  {
    path: 'briefings',
    element: <ProtectedRoute><BriefingList /></ProtectedRoute>,
  },
  {
    path: 'reports',
    element: <ProtectedRoute><TechReports /></ProtectedRoute>,
  },
];
